import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input, Label } from "../components/ui/input";
import { CheckCircle2, AlertCircle, ChevronsLeftRight } from "lucide-react";
import { toast } from "sonner";

/**
 * Public response page — NO authentication required.
 * Suppliers fill in their data via a token-gated form.
 */
export default function SupplierResponse() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [responder, setResponder] = useState({ responder_name: "", responder_email: "" });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);

  React.useEffect(() => {
    document.documentElement.classList.add("dark");
  }, []);

  useEffect(() => {
    // Use raw fetch since this is a public endpoint (no auth interceptor)
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/suppliers/respond/${token}`)
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then((d) => {
        setData(d);
        if (d.already_responded) setSubmitted(true);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    setBusy(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/suppliers/respond/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers, ...responder }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Failed");
      }
      setSubmitted(true);
      toast.success("Submitted — thank you");
    } catch (e) {
      toast.error(e.message || "Submission failed");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen bg-background text-foreground dark flex items-center justify-center font-mono text-sm text-muted-foreground">Loading…</div>;

  if (notFound) {
    return (
      <div className="min-h-screen bg-background text-foreground dark flex items-center justify-center p-6">
        <div className="card-flat p-12 max-w-md text-center">
          <AlertCircle className="h-10 w-10 mx-auto mb-3 text-red-400" />
          <h2 className="font-display text-2xl font-bold mb-2">Invalid link</h2>
          <p className="text-sm text-muted-foreground">This data request link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  const req = data.request;

  return (
    <div className="min-h-screen bg-background text-foreground dark">
      <header className="border-b border-border bg-card px-6 py-4 flex items-center gap-3">
        <div className="h-8 w-8 bg-accent flex items-center justify-center rounded-sm">
          <ChevronsLeftRight className="h-4 w-4 text-black" strokeWidth={2.5} />
        </div>
        <div>
          <div className="font-display font-bold tracking-tight">LESTARI</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Supplier Engagement Portal</div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-12">
        {submitted ? (
          <div className="card-flat p-12 text-center" data-testid="response-success">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-400" />
            <h1 className="font-display text-3xl font-bold mb-2">Thank you</h1>
            <p className="text-sm text-muted-foreground">Your response has been recorded for <strong>{data.requesting_entity}</strong>. No further action required.</p>
          </div>
        ) : (
          <>
            <div className="label-eyebrow mb-1">ESG data request</div>
            <h1 className="font-display text-3xl font-bold tracking-tight mb-2">{data.requesting_entity}</h1>
            <p className="text-sm text-muted-foreground mb-8">
              has requested ESG &amp; emissions data from <strong>{data.supplier_name}</strong> for reporting period <strong>{req.period}</strong>. Please complete the form below — it should take 5 minutes.
            </p>

            <div className="card-flat p-6 space-y-4 mb-4" data-testid="response-form">
              <div className="grid grid-cols-2 gap-3 pb-4 mb-2 border-b border-border">
                <div><Label>Your name</Label><Input value={responder.responder_name} onChange={(e) => setResponder({...responder, responder_name: e.target.value})} data-testid="responder-name" /></div>
                <div><Label>Your email</Label><Input type="email" value={responder.responder_email} onChange={(e) => setResponder({...responder, responder_email: e.target.value})} data-testid="responder-email" /></div>
              </div>

              {req.questions.map((q) => (
                <div key={q.key}>
                  <Label>{q.label}</Label>
                  <Input
                    type="number"
                    step="any"
                    value={answers[q.key] || ""}
                    onChange={(e) => setAnswers({...answers, [q.key]: e.target.value})}
                    data-testid={`q-${q.key}`}
                  />
                </div>
              ))}

              <Button variant="primary" size="lg" className="w-full mt-4" onClick={submit} disabled={busy} data-testid="response-submit-btn">
                {busy ? "Submitting…" : "Submit response"}
              </Button>
            </div>

            <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground text-center">
              Powered by Lestari · Singapore-residency cloud · This link is unique to your organisation.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
